import React, { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { LopSearchableSelect } from '../../components/LopSearchableSelect';
import type { MutualFund } from '../../types/MutualFund';
import type { SpecialCreationLimit } from '../../types/SpecialCreationLimit';
import { t } from '../../i18n/t';
import { LopButton } from '../../components/LopButton';
import './SpecialCreationLimitFormModal.css';

export interface SpecialCreationLimitFormModalProps {
  funds: MutualFund[];
  // null = add mode (fund picker open, empty limit). Non-null = edit mode
  // (fund picker locked to the existing fund — fundId is the natural PK,
  // changing it is semantically delete+add).
  initialRow: SpecialCreationLimit | null;
  submitting: boolean;
  submitError: string | null;
  onSubmit: (payload: SpecialCreationLimit) => void;
  onClose: () => void;
}

type LimitMode = 'qty' | 'value';

// This is the ONLY place a SpecialCreationLimit row is created or edited —
// there is deliberately no inline grid editing of limitQty/limitValue. That's
// what makes the "exactly one of the two" rule enforceable: the radio below
// shows a single input at a time and always sends the other one as null.
export function SpecialCreationLimitFormModal({
  funds,
  initialRow,
  submitting,
  submitError,
  onSubmit,
  onClose,
}: SpecialCreationLimitFormModalProps) {
  const isEdit = initialRow !== null;

  // The fund picker must show BOTH the fund name and its exchange number
  // (fundAssetId) — reuse LopSearchableSelect, but key it by fundAssetId
  // rather than fundId so the option label ("<fundAssetId> - <fundName>")
  // is the exchange number the user recognizes, not the internal fundId.
  // fundId (the PK this table actually stores) is resolved back via the map
  // below at submit time.
  const fundOptions = useMemo(
    () => funds.map((f) => ({ id: f.fundAssetId, name: f.fundName })),
    [funds],
  );
  const fundIdByAssetId = useMemo(
    () => new Map(funds.map((f) => [f.fundAssetId, f.fundId])),
    [funds],
  );
  const assetIdByFundId = useMemo(
    () => new Map(funds.map((f) => [f.fundId, f.fundAssetId])),
    [funds],
  );

  const [selectedFundAssetId, setSelectedFundAssetId] = useState<number | null>(
    () =>
      initialRow
        ? assetIdByFundId.get(initialRow.fundId) ?? initialRow.fundAssetId ?? null
        : null,
  );

  const [limitMode, setLimitMode] = useState<LimitMode>(
    () => (initialRow?.limitValue != null ? 'value' : 'qty'),
  );
  const [limitQtyStr, setLimitQtyStr] = useState(
    initialRow?.limitQty != null ? String(initialRow.limitQty) : '',
  );
  const [limitValueStr, setLimitValueStr] = useState(
    initialRow?.limitValue != null ? String(initialRow.limitValue) : '',
  );
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = () => {
    if (selectedFundAssetId == null) {
      setError(t('specialCreationLimits.form.validationFund'));
      return;
    }
    const fundId = fundIdByAssetId.get(selectedFundAssetId);
    if (fundId === undefined) {
      setError(t('specialCreationLimits.form.validationFund'));
      return;
    }

    let limitQty: number | null = null;
    let limitValue: number | null = null;
    if (limitMode === 'qty') {
      const qtyNum = Number(limitQtyStr);
      if (limitQtyStr.trim() === '' || Number.isNaN(qtyNum)) {
        setError(t('specialCreationLimits.form.validationAmount'));
        return;
      }
      limitQty = qtyNum;
    } else {
      const valueNum = Number(limitValueStr);
      if (limitValueStr.trim() === '' || Number.isNaN(valueNum)) {
        setError(t('specialCreationLimits.form.validationAmount'));
        return;
      }
      limitValue = valueNum;
    }

    setError(null);
    onSubmit({
      fundId,
      // fundName/fundAssetId are a read-only server-side projection (JOIN to
      // MutualFunds) — never written from here, mirrors CapitalConsolidations.
      fundName: null,
      fundAssetId: null,
      limitQty,
      limitValue,
    });
  };

  return (
    <div
      className="sclw__backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="sclw"
        role="dialog"
        aria-modal="true"
        aria-label={t(
          isEdit
            ? 'specialCreationLimits.form.editTitle'
            : 'specialCreationLimits.form.addTitle',
        )}
      >
        <header className="sclw__header">
          <h2 className="sclw__title">
            {t(
              isEdit
                ? 'specialCreationLimits.form.editTitle'
                : 'specialCreationLimits.form.addTitle',
            )}
          </h2>
          <button
            type="button"
            className="sclw__close"
            aria-label={t('recordForm.close')}
            onClick={onClose}
          >
            <X size={18} />
          </button>
        </header>

        <div className="sclw__body">
          <label className="sclw__field" htmlFor="scl-fund-picker">
            <span>{t('specialCreationLimits.form.fund')}</span>
            <LopSearchableSelect
              id="scl-fund-picker"
              value={selectedFundAssetId}
              options={fundOptions}
              onChange={setSelectedFundAssetId}
              disabled={isEdit}
              placeholder={t('specialCreationLimits.form.fundSearchPlaceholder')}
            />
          </label>

          <div className="sclw__field">
            <span>{t('specialCreationLimits.form.limitBy')}</span>
            <div className="sclw__radios">
              <label className="sclw__radio">
                <input
                  type="radio"
                  name="scl-limit-mode"
                  checked={limitMode === 'qty'}
                  onChange={() => setLimitMode('qty')}
                />
                <span>{t('specialCreationLimits.form.byQty')}</span>
              </label>
              <label className="sclw__radio">
                <input
                  type="radio"
                  name="scl-limit-mode"
                  checked={limitMode === 'value'}
                  onChange={() => setLimitMode('value')}
                />
                <span>{t('specialCreationLimits.form.byValue')}</span>
              </label>
            </div>
          </div>

          {limitMode === 'qty' ? (
            <label className="sclw__field">
              <span>{t('specialCreationLimits.columns.limitQty')}</span>
              <input
                type="number"
                value={limitQtyStr}
                onChange={(e) => setLimitQtyStr(e.target.value)}
                autoFocus
              />
            </label>
          ) : (
            <label className="sclw__field">
              <span>{t('specialCreationLimits.columns.limitValue')}</span>
              <input
                type="number"
                value={limitValueStr}
                onChange={(e) => setLimitValueStr(e.target.value)}
                autoFocus
              />
            </label>
          )}
        </div>

        {(error || submitError) && (
          <div className="sclw__error">{error ?? submitError}</div>
        )}

        <footer className="sclw__footer">
          <LopButton variant="primary" onClick={handleSubmit} loading={submitting}>
            {t('specialCreationLimits.form.submit')}
          </LopButton>
          <LopButton variant="secondary" onClick={onClose} disabled={submitting}>
            {t('recordForm.cancel')}
          </LopButton>
        </footer>
      </div>
    </div>
  );
}
